import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  id?: string
  name?: string
  onBlur?: () => void
  required?: boolean
  maxDate?: Date
  minDate?: Date
}

export function DatePicker({
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  disabled = false,
  className,
  id,
  name,
  onBlur,
  required,
  maxDate,
  minDate,
}: DatePickerProps) {
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(value)
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    setSelectedDate(value)
  }, [value])

  const handleSelect = (date: Date | undefined) => {
    setSelectedDate(date)
    // Ensure we're passing a Date object, not undefined
    if (date) {
      onChange?.(date)
    } else {
      onChange?.(undefined)
    }
    setOpen(false)
  }

  const formatDate = (date: Date | undefined) => {
    if (!date) return ""
    return format(date, "MMM dd, yyyy")
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-between text-left font-normal h-9",
            !selectedDate && "text-muted-foreground",
            className
          )}
          disabled={disabled}
          id={id}
          name={name}
          onBlur={onBlur}
        >
          <div className="flex items-center flex-1 min-w-0">
            <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate">
              {selectedDate ? formatDate(selectedDate) : placeholder}
            </span>
          </div>
          {required && !selectedDate && (
            <span className="text-destructive ml-1 shrink-0">*</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          initialFocus
          disabled={(date) => {
            if (disabled) return true;
            if (maxDate && date > maxDate) return true;
            if (minDate && date < minDate) return true;
            return false;
          }}
          captionLayout="dropdown"
          fromYear={minDate ? minDate.getFullYear() : 1900}
          toYear={maxDate ? maxDate.getFullYear() : new Date().getFullYear() + 10}
        />
      </PopoverContent>
    </Popover>
  )
}

// Controller wrapper for react-hook-form
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form"

interface DatePickerControllerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  control: Control<TFieldValues>
  name: TName
  label?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  required?: boolean
  maxDate?: Date
  minDate?: Date
}

export function DatePickerController<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  placeholder,
  disabled = false,
  className,
  required = false,
  maxDate,
  minDate,
}: DatePickerControllerProps) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <div className={cn("space-y-2", className)}>
          {label && (
            <label htmlFor={name} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              {label}
              {required && <span className="text-destructive ml-1">*</span>}
            </label>
          )}
          <DatePicker
            value={field.value ? new Date(field.value) : undefined}
            onChange={(date) => {
              field.onChange(date ? format(date, "yyyy-MM-dd") : "")
            }}
            placeholder={placeholder}
            disabled={disabled}
            id={name}
            onBlur={field.onBlur}
            required={required}
            maxDate={maxDate}
            minDate={minDate}
          />
          {error && (
            <p className="text-sm font-medium text-destructive">
              {error.message}
            </p>
          )}
        </div>
      )}
    />
  )
}